/**
 * The company's jurisdiction (#181) and its VAT rates.
 *
 * Server contract (packages/api/src/routes/settings/vat-rates.routes.ts,
 * packages/api/src/routes/public/index.ts):
 *   GET  /public/jurisdictions           (no auth)  countries a company may sign up in
 *   GET  /settings/jurisdiction          (any user) the country and what it offers
 *   POST /settings/jurisdiction          (tenant:settings) { jurisdiction, reason }
 *   GET  /settings/vat-rates             (any user) every rate, withdrawn ones included
 *   POST /settings/vat-rates             (tenant:settings) { code, label, rate, invoiceMention? }
 *   POST /settings/vat-rates/:id/withdraw (tenant:settings)
 *
 * A screen switches to the GENERIC_VAT behaviour only on a loaded state
 * whose `taxScheme` says so (`isGenericVat`): while the state is loading or
 * unreadable, every screen renders as for Burkina Faso.
 */

import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TaxScheme } from '@efacture/core';
import { apiClient } from '@/api/client';

// ─────────────────────────────────────────────────────────────
// Wire shapes
// ─────────────────────────────────────────────────────────────

/** The surfaces a jurisdiction may not provide (api jurisdiction-gate.ts). */
export type JurisdictionSurface =
  | 'POS'
  | 'HOTEL_CHECKOUT'
  | 'EXTERNAL_SALES'
  | 'FISCAL_REPORTS'
  | 'FEC'
  | 'STANDALONE_CREDIT_NOTE'
  | 'RECURRING_INVOICES'
  | 'DGI_PARAMETER_TABLES'
  | 'MCF'
  | 'NODE_ENROLMENT'
  | 'PAYROLL'
  | 'DECLARATIONS';

export type JurisdictionSurfaces = Readonly<Record<JurisdictionSurface, boolean>>;

/** GET /settings/jurisdiction */
export interface JurisdictionState {
  jurisdiction: string;
  name: string;
  /** Whether the jurisdiction can be changed now. */
  allowed: boolean;
  /** Why not, in French; null when allowed. */
  reason: string | null;
  /** Leaving this jurisdiction is done by the platform operator. */
  platformOnly: boolean;
  taxScheme: TaxScheme;
  /** Printed label of the seller's tax identifier (« IFU » in Burkina Faso). */
  taxIdentifierLabel: string;
  /** Invoice types offered (FV, FT, FA, …). */
  documentTypes: string[];
  surfaces: JurisdictionSurfaces;
}

/** One entry of GET /public/jurisdictions. */
export interface PublicJurisdiction {
  code: string;
  name: string;
  taxIdentifierLabel: string;
  /** Whether the signup asks for the tax regime (Burkina Faso only). */
  asksTaxRegime: boolean;
}

/** POST /settings/jurisdiction */
export interface JurisdictionChangeResult {
  jurisdiction: string;
  previous: string;
  /** Articles whose tax code is not one of the new scheme. */
  articlesToReview: number;
}

/** A company VAT rate; `rate` is a fraction (0.18 = 18 %). */
export interface VatRate {
  id: string;
  code: string;
  label: string;
  rate: number;
  invoiceMention: string | null;
  createdAt: string;
  withdrawnAt: string | null;
}

export interface CreateVatRateInput {
  code: string;
  label: string;
  /** Fraction, 0 <= rate < 1. */
  rate: number;
  invoiceMention?: string | null;
}

// ─────────────────────────────────────────────────────────────
// Readers
// ─────────────────────────────────────────────────────────────

/** True when the loaded state is a GENERIC_VAT jurisdiction. */
export function isGenericVat(state: Pick<JurisdictionState, 'taxScheme'> | null | undefined): boolean {
  return state?.taxScheme === 'GENERIC_VAT';
}

/** The surfaces of a loaded state, or null (nothing hidden) while unknown. */
export function surfacesOf(state: JurisdictionState | null | undefined): JurisdictionSurfaces | null {
  return state?.surfaces ?? null;
}

/** False only when a loaded state says the surface is not provided. */
export function surfaceAvailable(
  state: JurisdictionState | null | undefined,
  surface: JurisdictionSurface
): boolean {
  return state?.surfaces?.[surface] !== false;
}

/** The rates a new line or article may name: those not withdrawn. */
export function activeVatRates(rates: readonly VatRate[] | null | undefined): VatRate[] {
  return (rates ?? []).filter((rate) => rate.withdrawnAt === null);
}

/** « 18 » for 0.18, « 19,25 » for 0.1925 — the percentage without a float tail. */
export function vatRatePercent(rate: number): string {
  return String(Math.round(rate * 10_000) / 100).replace('.', ',');
}

/** « TVA18 — TVA normale (18 %) » */
export function vatRateLabel(rate: Pick<VatRate, 'code' | 'label' | 'rate'>): string {
  return `${rate.code} — ${rate.label} (${vatRatePercent(rate.rate)} %)`;
}

// ─────────────────────────────────────────────────────────────
// Queries
// ─────────────────────────────────────────────────────────────

export const PUBLIC_JURISDICTIONS_PATH = '/public/jurisdictions';

export const jurisdictionKeys = {
  all: ['jurisdiction'] as const,
  state: () => [...jurisdictionKeys.all, 'state'] as const,
  publicList: () => [...jurisdictionKeys.all, 'public'] as const,
  vatRates: () => [...jurisdictionKeys.all, 'vat-rates'] as const,
};

/** The company's jurisdiction — GET /api/settings/jurisdiction */
export function useJurisdiction(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: jurisdictionKeys.state(),
    queryFn: () => apiClient.get<JurisdictionState>('/settings/jurisdiction'),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
}

/** Countries a company may be in — GET /api/public/jurisdictions (no auth). */
export function usePublicJurisdictions() {
  return useQuery({
    queryKey: jurisdictionKeys.publicList(),
    queryFn: () => apiClient.get<{ jurisdictions: PublicJurisdiction[] }>(PUBLIC_JURISDICTIONS_PATH),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/**
 * Changes the company's country — POST /api/settings/jurisdiction. The
 * country decides the forms, the menu and the tax engine of every screen,
 * so every cached query is refetched afterwards.
 */
export function useChangeJurisdiction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { jurisdiction: string; reason: string }) =>
      apiClient.post<JurisdictionChangeResult>('/settings/jurisdiction', input),
    onSuccess: () => {
      void queryClient.invalidateQueries();
    },
  });
}

/** The company's VAT rates, withdrawn ones included — GET /api/settings/vat-rates */
export function useVatRates(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: jurisdictionKeys.vatRates(),
    queryFn: () => apiClient.get<{ vatRates: VatRate[] }>('/settings/vat-rates'),
    enabled: options?.enabled ?? true,
  });
}

/** Defines a VAT rate — POST /api/settings/vat-rates */
export function useCreateVatRate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateVatRateInput) => apiClient.post<VatRate>('/settings/vat-rates', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jurisdictionKeys.vatRates() });
    },
  });
}

/** Withdraws a VAT rate — POST /api/settings/vat-rates/:id/withdraw */
export function useWithdrawVatRate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.post<VatRate>(`/settings/vat-rates/${id}/withdraw`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jurisdictionKeys.vatRates() });
    },
  });
}

/**
 * The company's GENERIC_VAT context for a form: whether it applies, and the
 * active rates. The rates are read only for a GENERIC_VAT company.
 */
export function useGenericVatContext() {
  const jurisdiction = useJurisdiction();
  const generic = isGenericVat(jurisdiction.data);
  const rates = useVatRates({ enabled: generic });
  const activeRates = useMemo(
    () => (generic ? activeVatRates(rates.data?.vatRates) : []),
    [generic, rates.data]
  );
  return {
    jurisdiction: jurisdiction.data ?? null,
    generic,
    activeRates,
    ratesLoaded: !generic || rates.data !== undefined,
  };
}
